import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import PersonAddIcon from '@mui/icons-material/PersonAdd';

import api from '../../utils/functions/api';
import { openWindow, closeWindow } from '../../utils/functions/windowManager';
import HrMonthlySalarySummaryDetail from './HrMonthlySalarySummaryDetail';
import ExcludedEmployeeModal from './monthlySalarySummary/ExcludedEmployeeModal';
import ExternalPersonModal from './monthlySalarySummary/ExternalPersonModal';
import MonthListSkeleton from './monthlySalarySummary/MonthListSkeleton';
import MonthRow from './monthlySalarySummary/MonthRow';
import { monthLabel } from './monthlySalarySummary/utils';

export default function HrMonthlySalarySummary() {
  const theme = useTheme();
  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [externalOpen, setExternalOpen] = useState(false);
  const [excludedOpen, setExcludedOpen] = useState(false);

  useEffect(() => {
    const fetchMonths = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`${process.env.REACT_APP_API_URL}/monthly-salary-summary`);
        setMonths(data || []);
      } finally {
        setLoading(false);
      }
    };
    fetchMonths();
  }, []);

  const handleOpenDetail = (year, month) => {
    const winId = `monthly-salary-${year}-${month}`;
    openWindow({
      id: winId,
      title: `สรุปเงินเดือนรวม ${monthLabel(year, month)}`,
      defaultWidth: 1300,
      defaultHeight: 820,
      onClose: () => closeWindow(winId),
      render: () => (
        <div style={{ padding: '12px 20px', overflowY: 'auto', flex: 1 }}>
          <HrMonthlySalarySummaryDetail year={year} month={month} />
        </div>
      ),
    });
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }} flexWrap="wrap" rowGap={1}>
        <Stack spacing={0.5}>
          <Typography variant="h5" fontWeight={700}>สรุปเงินเดือนรวมทั้งเดือน</Typography>
          <Typography variant="body2" color="text.secondary">
            รวมยอดเงินเดือนของทั้งเดือนต่อพนักงาน หลังบันทึกเงินเดือนครบทุกงวด — กดปุ่มจัดการท้ายแถวเพื่อดูรายละเอียดหรือส่งออกไฟล์ ปกส.
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<PersonAddIcon fontSize="small" />} onClick={() => setExternalOpen(true)}>
            รายชื่อคนนอก (ฝากส่ง ปกส.)
          </Button>
          <Button variant="outlined" color="error" startIcon={<BlockIcon fontSize="small" />} onClick={() => setExcludedOpen(true)}>
            พนักงานที่ไม่ต้องส่ง ปกส.
          </Button>
        </Stack>
      </Stack>

      {loading ? (
        <MonthListSkeleton />
      ) : (
        <Card elevation={0} sx={{ borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08) }}>
                  <TableCell sx={{ fontWeight: 700 }}>เดือน</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>ยอดจ่ายรวม</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>รายวัน</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>รายเดือน</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>งวดที่บันทึก</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>สถานะ</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>จัดการ</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {months.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Stack spacing={0.5} alignItems="center" sx={{ py: 4, color: 'text.disabled' }}>
                        <EventBusyIcon />
                        <Typography variant="body2">ยังไม่มีข้อมูลเงินเดือนที่บันทึกไว้</Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                )}
                {months.map((m, idx) => (
                  <MonthRow key={`${m.year}-${m.month}`} month={m} index={idx} onOpen={handleOpenDetail} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      <ExternalPersonModal open={externalOpen} onClose={() => setExternalOpen(false)} />
      <ExcludedEmployeeModal open={excludedOpen} onClose={() => setExcludedOpen(false)} />
    </Box>
  );
}
